import { forwardRef, ComponentProps } from "react";

export type Props = Omit<ComponentProps<"input">, "onChange"> & {
  /** Field name for identification */
  name: string;
  /** Current value */
  value?: string;
  /** Change handler */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Blur handler */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Whether the field has an error (for styling and aria-invalid) */
  error?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** ID of the element that describes this input (typically error message) */
  "aria-describedby"?: string;
  /** ID of the element that labels this input */
  "aria-labelledby"?: string;
};

/**
 * Base input component with no styling.
 * Use this as the foundation for styled input components.
 */
export const InputBase = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      value,
      onChange,
      onBlur,
      error,
      disabled,
      type = "text",
      className,
      required: _required, // Filtered to prevent HTML5 validation bubbles
      ...props
    },
    ref
  ) => {
    return (
      <input
        ref={ref}
        name={name}
        id={name}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        aria-invalid={error || undefined}
        className={className}
        data-testid={`input-${name}`}
        {...props}
      />
    );
  }
);

InputBase.displayName = "InputBase";

